import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { showToast } from '../lib/toast';
import { boxSizingStyles } from '../styles/lit-shared';
import './ui/ui-button';

const DEFAULT_EMAIL = 'soporte@alejandrosn.com';

async function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const input = document.createElement('textarea');
  input.value = text;
  input.setAttribute('readonly', '');
  input.style.position = 'fixed';
  input.style.left = '-9999px';
  document.body.appendChild(input);
  input.select();
  const ok = document.execCommand('copy');
  input.remove();
  if (!ok) throw new Error('copy failed');
}

@customElement('copy-email-button')
export class CopyEmailButton extends LitElement {
  @property({ type: String }) email = DEFAULT_EMAIL;
  @state() private copied = false;
  private _resetTimer: number | null = null;

  updated() {
    this.title = `Copiar ${this.email}`;
  }

  disconnectedCallback() {
    if (this._resetTimer !== null) {
      window.clearTimeout(this._resetTimer);
      this._resetTimer = null;
    }
    super.disconnectedCallback();
  }

  private async copyEmail() {
    try {
      await copyToClipboard(this.email);
    } catch {
      showToast('No se pudo copiar el email. Cópialo a mano.');
      return;
    }

    this.copied = true;
    showToast('Email copiado al portapapeles');

    if (this._resetTimer !== null) window.clearTimeout(this._resetTimer);
    this._resetTimer = window.setTimeout(() => {
      this.copied = false;
      this._resetTimer = null;
    }, 2000);
  }

  private renderIcon() {
    if (this.copied) {
      return html`
        <svg
          class="icon"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      `;
    }

    return html`
      <svg
        class="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
        <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
      </svg>
    `;
  }

  static styles = [
    boxSizingStyles,
    css`
      :host {
        display: inline-flex;
      }

      .icon {
        width: 1rem;
        height: 1rem;
        flex-shrink: 0;
      }
    `,
  ];

  render() {
    return html`
      <ui-button variant="secondary" @ui-click=${() => void this.copyEmail()}>
        ${this.renderIcon()}
        ${this.email}
      </ui-button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'copy-email-button': CopyEmailButton;
  }
}
